'use client';

import { usePathname } from 'next/navigation';

import { Separator } from '../Decorators/Separator/Separator';
import {
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationWrapper,
} from './PaginationComponents';

interface PaginationProps {
  className?: string;
  pages?: number;
  activePage?: number;
  visibleCount?: number;
}

const Pagination = ({
  className,
  pages = 1,
  activePage = 1,
  visibleCount = 5,
}: PaginationProps) => {
  const pathname = usePathname();

  if (!pages) return null;

  const generateHref = (page: number) => {
    if (pathname.includes('/page/')) {
      return pathname.replace(/\/page\/\d+$/, `/page/${page}`);
    }

    return `${pathname}/page/${page}`;
  };

  const { floor, min, max } = Math;
  const range = (lo: number, hi: number) => Array.from({ length: hi - lo }, (_, i) => i + lo);

  const pagination =
    (count: number, ellipsis: string = '…') =>
    (page: number, total: number) => {
      const start = max(1, min(page - floor((count - 3) / 2), total - count + 2));
      const end = min(total, max(page + floor((count - 2) / 2), count - 1));

      return [
        ...(start > 2 ? [1, ellipsis] : start > 1 ? [1] : []),
        ...range(start, end + 1),

        ...(end < total - 1 ? [ellipsis, total] : end < total ? [total] : []),
      ];
    };

  const pagesToShow = pagination(visibleCount)(activePage, pages);

  return (
    <div className={className}>
      <Separator className="mb-4" />
      <PaginationWrapper>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href={generateHref(Math.max(1, activePage - 1))}
              isActive={activePage > 1}
            />
          </PaginationItem>

          {pagesToShow.map(item =>
            typeof item === 'number' ? (
              <PaginationItem key={`page-${item}`} className="hidden md:block">
                <PaginationLink href={generateHref(item)} isActive={item === activePage}>
                  {item}
                </PaginationLink>
              </PaginationItem>
            ) : (
              <PaginationItem key={`ellipsis-${item}`} className="hidden md:block">
                <PaginationEllipsis />
              </PaginationItem>
            ),
          )}

          <PaginationItem>
            <PaginationNext
              href={generateHref(Math.min(pages, activePage + 1))}
              isActive={activePage < pages}
            />
          </PaginationItem>
        </PaginationContent>
      </PaginationWrapper>
    </div>
  );
};

export { Pagination };
